import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useSnackbar } from "notistack";
import axios from "axios";
import { Container, Box, Grid } from "@mui/material";
import { withPather } from "react-pather";
import Result from "./result";
import DescriptionInput from "./DescriptionInput";
import Period from "./Period";
import Advanced from "./advanced";
import StaticDatePicker from "./StaticDatePicker";
import ShortenBtn from "./ShortenBtn";
import Slug from "./slug";
import { reservedSlugMode as reservedAutoCodeSlugMode } from "./slug/AutoCode";
import { reservedSlugMode as reservedSmartCodeSlugMode } from "./slug/SmartCode";
import { reservedSlugMode as reservedCustomCodeSlugMode } from "./slug/CustomCode";
import Loader from "../Loader";
import { __DEV__, UUT, UUTLC, RESERVED_CUSTOM_CODES } from "../../config";
import { URLInput } from "../general";
import { isAdmin } from "../../utils";
import { useEffectExceptOnMount } from "../../hooks";
import { setToken } from "../../slices/tokenSlice";

const DEFAULT_PERIOD_DAYS = 181;

const Shorten = ({ pather }) => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();

  // Main basic settings
  const [url, setUrl] = useState("");
  const [days, setDays] = useState(DEFAULT_PERIOD_DAYS);
  const [code, setCode] = useState(""); // <-| Saves code that is already assigned to a Conversion
  const [description, setDescription] = useState("");
  const [favourite, setFavourite] = useState(false);
  const [slugMode, setSlugMode] = useState(reservedAutoCodeSlugMode);
  const user = useSelector((state) => state.user.value);

  // Booleans
  const [showResult, setShowResult] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isAdvancedExpanded, setAdvancedExpanded] = useState(false);
  const [isSlugSettingsExpanded, setSlugSettingsExpanded] = useState(false);
  const [isClone, setIsClone] = useState(false);

  const [isPathOccupied, setIsPathOccupied] = useState(false);
  const [loadingCustomCodeCheck, setLoadingCustomCodeCheck] = useState(false);
  const [isCustomCodeInvalid, setIsCustomCodeInvalid] = useState(false);
  //

  // Storing Advanced settings
  const [advanced, setAdvanced] = useState({
    maxRedirects: null,
    revealable: true,
    customCode: "",
    isDead: false,
    passcode: null,
    isSmartCode: false,
    tags: [],
  });

  const setAdvancedSafe = (updStateObj) => {
    setAdvanced((prevState) => ({
      ...prevState,
      ...updStateObj,
    }));
  };
  //

  // Additional Settings (those that are related to advanced but aren't used in the request)
  const [additional, setAdditional] = useState({
    isCustomCodeAvailable: null, // null - no info, true - available, false - not available
    notifying: false,
    locking: false,
  });

  const setAdditionalSafe = (updStateObj) => {
    setAdditional((prevState) => ({
      ...prevState,
      ...updStateObj,
    }));
  };
  //

  useEffect(() => {
    // Reset prev values
    if (isCustomCodeInvalid) setIsCustomCodeInvalid(false);
    if (additional.isCustomCodeAvailable !== null)
      setAdditionalSafe({ isCustomCodeAvailable: null });
    if (isPathOccupied) setIsPathOccupied(false);

    // When custom code is ''
    if (!advanced.customCode) {
      setIsCustomCodeInvalid(false);
      return;
    }

    //  Check code validity
    if (!isCodeValid(advanced.customCode)) {
      setIsCustomCodeInvalid(true);
      return;
    }

    // Check code availability
    checkCodeAvalability();
  }, [advanced.customCode]);

  useEffect(() => {
    if (__DEV__ && url === "test") setUrl("https://react.dev/");
  }, [url]);

  useEffectExceptOnMount(() => {
    setAdvancedSafe({ isSmartCode: slugMode === reservedSmartCodeSlugMode });
    if (slugMode !== reservedCustomCodeSlugMode)
      setAdvancedSafe({ customCode: "" });
  }, [slugMode]);

  const checkCodeAvalability = () => {
    if (advanced.customCode === code) {
      setAdditionalSafe({ isCustomCodeAvailable: true });
      return;
    }
    setLoadingCustomCodeCheck(true);
    axios
      .get(
        pather.reverse(pather.back.Conversion.redirectData, {
          code: advanced.customCode.trim(),
        })
      )
      .then((res) => {
        setAdditionalSafe({
          isCustomCodeAvailable: !!res.data.isCodeAvailable,
        });
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setLoadingCustomCodeCheck(false);
      });
  };

  const pathExists = (code) => {
    const front = pather.front;
    for (const prop in front) {
      if (Object.prototype.hasOwnProperty.call(front, prop)) {
        if (prop === code) {
          setIsPathOccupied(true);
          return true;
        }
        if (
          typeof front[prop] === "string" &&
          (front[prop].match(/\//g) || []).length === 1
        ) {
          // If the amount of forward slashes is equal to 1
          const clearPath = front[prop].replace(/\//g, "");
          if (clearPath === code) {
            setIsPathOccupied(true);
            return true;
          }
          for (const value of RESERVED_CUSTOM_CODES) {
            if ([value, `${value}s`, `${value}es`].includes(code)) {
              setIsPathOccupied(true);
              return true;
            }
          }
        }
      }
    }
    setIsPathOccupied(false);
    return false;
  };

  // Custom Code (CC) Validation
  const isCodeValid = (code, notify = false) => {
    if (!code) {
      notify &&
        enqueueSnackbar("Custom code is not provided", { variant: "error" });
      return false;
    } else if (code.length < 5) {
      notify &&
        enqueueSnackbar("Custom code must be at least 5 characters long", {
          variant: "error",
        });
      return false;
    } else if (code.length > 100) {
      notify &&
        enqueueSnackbar("Custom code must not exceed 100 characters", {
          variant: "error",
        });
      return false;
    } else if (!/^[A-Za-z0-9-_]*$/.test(code)) {
      notify && enqueueSnackbar("Invalid custom code", { variant: "error" });
      return false;
    } else if (pathExists(code)) {
      notify &&
        enqueueSnackbar("The custom code is reserved", { variant: "error" });
      return false;
    }
    return true;
  };

  // Other fields validation
  const isFieldsValid = () => {
    if (!!!url.trim()) {
      enqueueSnackbar("URL not provided", { variant: "error" });
      return false;
    } else if (!pather.isURLValid(url.trim(), true)) {
      enqueueSnackbar("Invalid URL entered", { variant: "error" });
      return false;
    } else if (days !== "" && days <= 0) {
      enqueueSnackbar("Duration must be greater than 0 days", {
        variant: "error",
      });
      return false;
    } else if (days !== "" && days > 50000) {
      enqueueSnackbar("Duration cannot exceed 50000 days", {
        variant: "error",
      });
      return false;
    } else if (!user?.isLogined && days > 365) {
      // 1 year
      enqueueSnackbar(
        "Authentication is required to shorten for longer periods",
        { variant: "error" }
      );
      return false;
    } else if (advanced.maxRedirects != null) {
      if (
        advanced.maxRedirects <= 0 ||
        isNaN(parseInt(advanced.maxRedirects))
      ) {
        enqueueSnackbar("Maximum number of redirects must be greater than 0", {
          variant: "error",
        });
        return false;
      } else if (advanced.maxRedirects > 1000000) {
        enqueueSnackbar("Number of maximum redirects cannot exceed 1000000", {
          variant: "error",
        });
        return false;
      }
    } else if (slugMode === reservedCustomCodeSlugMode) {
      const code = advanced.customCode;
      if (!isCodeValid(code, true)) return false;
      else if (
        (!!!days || days > 100) &&
        code.length >= 5 &&
        code.length <= 10 &&
        !isAdmin()
      ) {
        enqueueSnackbar(
          "Link with custom code between 5 and 10 characters (inclusive) can only be active for up to 100 days",
          { variant: "error" }
        );
        return false;
      }
    } else if (advanced.passcode !== null) {
      if (advanced.passcode === "") {
        enqueueSnackbar("Passcode is not set", { variant: "error" });
        return false;
      } else if (advanced.passcode.length < 5) {
        enqueueSnackbar("Passcode must be at least 5 characters long", {
          variant: "error",
        });
        return false;
      }
    }
    return true;
  };
  //

  const shorten = () => {
    if (!isFieldsValid()) return;
    setIsLoading(true);
    !!code ? edit() : create();
    window.scrollTo({ top: 0, behavior: "instant" }); // Scroll to Top
  };

  const create = () => {
    const tagCodes = advanced.tags.map((tag) => tag.code);
    axios
      .post(pather.back.Conversion.shorten, {
        source: url,
        description: description.trim(),
        days,
        ...advanced,
        tags: tagCodes,
      })
      .then((response) => {
        const data = response.data;
        setCode(data.code);
        setShowResult(true);
        data?.is_clone && setIsClone(true);
        const uut = localStorage.getItem(UUTLC);
        if (!uut) {
          dispatch(setToken(data.token));
          axios.defaults.headers.common[UUT] = data.token;
        }
      })
      .catch((error) => {
        console.log(error);
        const status = error.response.data.status;
        if (!!status) {
          enqueueSnackbar(status, { variant: "error" });
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const edit = () => {
    const tagsCodes = advanced.tags.map((tag) => tag.code);
    axios
      .put(pather.reverse(pather.back.Conversion.code, { code }), {
        source: url,
        description: description.trim(),
        days,
        slugMode,
        ...advanced,
        tags: tagsCodes,
      })
      .then((response) => {
        const data = response.data;
        if (!!data.new_code) setCode(data.new_code);
        setShowResult(true);
      })
      .catch((error) => {
        console.log(error);
        const status = error.response.data.status;
        if (!!status) enqueueSnackbar(status, { variant: "error" });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleRegenerate = () => {
    setIsLoading(true);
    axios
      .post(pather.reverse(pather.back.Conversion.regenerate, { code }), {
        source: url,
        days,
      })
      .then((response) => {
        setCode(response.data.code);
        setShowResult(true);
        resetCustomCode();
        setAdvancedSafe({ isSmartCode: false });
        setSlugMode(reservedAutoCodeSlugMode);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleClear = () => {
    setCode("");
    setShowResult(false);
    setDays(DEFAULT_PERIOD_DAYS);
    setUrl("");
    setDescription("");
    setIsClone(false);
    setAdvancedSafe({
      maxRedirects: null,
      revealable: true,
      customCode: "",
      isDead: false,
      passcode: null,
      isSmartCode: false,
      tags: [],
    });
    setAdditionalSafe({
      locking: false,
    });
    setSlugSettingsExpanded(false);
    setSlugMode(reservedAutoCodeSlugMode);
  };

  const resetCustomCode = () => {
    setAdvancedSafe({
      customCode: "",
    });
  };

  return (
    <Container sx={{ height: "100%" }}>
      {isLoading ? (
        <Loader boxMinHeight="550px" />
      ) : (
        <>
          {showResult ? (
            <Result
              shortenedUrl={`${window.location.origin}${pather.reverse(
                pather.front.main,
                { code }
              )}`}
              handleRegenerate={handleRegenerate}
              handleClear={handleClear}
              setShowResult={setShowResult}
              code={code}
              favourite={favourite}
              setFavourite={setFavourite}
              isClone={isClone}
            />
          ) : (
            <Box sx={{ mb: "5vw" }}>
              <HiddenTitle />
              <ShortenMain
                url={url}
                setUrl={setUrl}
                days={days}
                setDays={setDays}
              />
              <ShortenExtended
                advanced={advanced}
                setAdvancedSafe={setAdvancedSafe}
                isSlugSettingsExpanded={isSlugSettingsExpanded}
                setSlugSettingsExpanded={setSlugSettingsExpanded}
                loadingCustomCodeCheck={loadingCustomCodeCheck}
                isCustomCodeInvalid={isCustomCodeInvalid}
                additional={additional}
                setAdditionalSafe={setAdditionalSafe}
                isPathOccupied={isPathOccupied}
                slugMode={slugMode}
                setSlugMode={setSlugMode}
                isAdvancedExpanded={isAdvancedExpanded}
                setAdvancedExpanded={setAdvancedExpanded}
                description={description}
                setDescription={setDescription}
              />
              <ShortenBtn shorten={shorten} />
            </Box>
          )}
        </>
      )}
    </Container>
  );
};

const HiddenTitle = () => {
  return (
    <h1
      style={{
        position: "absolute",
        width: "1px",
        height: "1px",
        padding: 0,
        margin: "-1px",
        overflow: "hidden",
        clip: "rect(0, 0, 0, 0)",
        whiteSpace: "nowrap",
        border: 0,
      }}
    >
      Shorten Your URL
    </h1>
  );
};

const ShortenMain = ({ url, setUrl, days, setDays }) => {
  return (
    <Box sx={{ mt: 10 }}>
      <Grid container spacing={4}>
        <Grid item xs={12} md={12}>
          <URLInput url={url} setUrl={setUrl} />
        </Grid>
        <Grid item xs={12} md={6}>
          <Period
            days={days}
            handleDaysChange={(e) => setDays(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <StaticDatePicker days={days} setDays={setDays} />
        </Grid>
      </Grid>
    </Box>
  );
};

const ShortenExtended = (props) => {
  const {
    advanced,
    setAdvancedSafe,
    isSlugSettingsExpanded,
    setSlugSettingsExpanded,
    loadingCustomCodeCheck,
    isCustomCodeInvalid,
    additional,
    setAdditionalSafe,
    isPathOccupied,
    slugMode,
    setSlugMode,
    isAdvancedExpanded,
    setAdvancedExpanded,
    description,
    setDescription,
  } = props;

  const user = useSelector((state) => state.user.value);

  return (
    <Box sx={{ mt: "2vw" }}>
      {user?.isLogined && (
        <>
          <DescriptionInput
            description={description}
            setDescription={setDescription}
          />
          <Slug
            advanced={advanced}
            setAdvancedSafe={setAdvancedSafe}
            isSlugSettingsExpanded={isSlugSettingsExpanded}
            setSlugSettingsExpanded={setSlugSettingsExpanded}
            loadingCustomCodeCheck={loadingCustomCodeCheck}
            isCustomCodeInvalid={isCustomCodeInvalid}
            additional={additional}
            isPathOccupied={isPathOccupied}
            slugMode={slugMode}
            setSlugMode={setSlugMode}
          />
        </>
      )}
      <Advanced
        advanced={advanced}
        setAdvancedSafe={setAdvancedSafe}
        isAdvancedExpanded={isAdvancedExpanded}
        setAdvancedExpanded={setAdvancedExpanded}
        additional={additional}
        setAdditionalSafe={setAdditionalSafe}
      />
    </Box>
  );
};

export default withPather()(Shorten);